import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  DataCenterFileFilter as DataCenterFileFilterModel,
  DataCenterFileListDto,
  DataCenterFileListFilter,
  DataCenterFileListResource,
  Permission,
  PermissionAction,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { dataCenterFileCreatePageRoute } from "../DataCenterFileCreatePage/DataCenterFileCreatePage";
import { DataCenterFileFilter } from "../DataCenterFileFilter";
import { DataCenterFileTable } from "./DataCenterFileTable";

const routeName = "dataCenterFiles";

export const dataCenterFileListPageRoute: PageRoute = {
  handle: "Data Center",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const DataCenterFileListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.DataCenterFile;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<DataCenterFileListFilter>({
    sortObjects: [{ property: pn<DataCenterFileListDto>("date"), isDesc: true }],
  });
  const [initListFilter] = useState<DataCenterFileListFilter>(listFilter);
  const [listItems, setListItems] = useState<DataCenterFileListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<DataCenterFileListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.dataCenterFiles.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.dataCenterFiles.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.dataCenterFiles.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<DataCenterFileListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <DataCenterFileFilter
        filter={listFilter.dataCenterFileFilter || ({} as DataCenterFileFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.dataCenterFileFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{dataCenterFileListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button onClick={() => navigate(dataCenterFileCreatePageRoute.getPath())} variant="outlined">
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <DataCenterFileTable
              tableContainerRef={tableContainerRef}
              dataCenterFiles={listItems}
              resource={resource}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
